<template>
  <div class="search-results-container header--buffer">
    <SearchAgentOverlay ref="searchAgentOverlay" />
    <div class="search-results-container__results">
      <div
        class="search-results-container__top-container"
        id="SearchResultsTopContainer"
      >
        <!-- Antal annoncer her -->
        <div
          v-if="numberOfResults > 0"
          class="search-results-container__ad-count"
        >
          {{ thousandSeparator(numberOfResults) }}
          {{ productTypeName }} matcher din søgning
        </div>
        <div class="search-results-container__buttons">
          <!-- Sortér her -->
          <div
            class="search-results-container__button-container overlay--dropdown"
          >
            <Sorting
              v-if="sortItemsList !== null"
              :event-bus="proxyBus"
              :items="sortItemsList"
            />
          </div>

          <div class="search-results-container__button-container">
            <FilteringMenu
              v-if="!hideFiltering"
              :is-child-open="isChildOpen"
              :event-bus="eventBus"
            >
              <template v-slot:content>
                <Summary :summary-items="summaryItems" />
                <Filtering
                  :ui-prop="ui"
                  :event-bus="eventBus"
                  :search-request="searchRequest"
                  :ads-count="adsCount"
                  :product-type-name-singular="productTypeNameSingular"
                  :product-type-name-plural="productTypeNamePlural"
                />
              </template>
              <template v-slot:button-open-content>
                <div class="search-results-container__button">
                  <img
                    loading="lazy"
                    class="search-results-container__button-icon"
                    src="/static/icons/adjust.svg"
                    alt="Filtrér"
                  />
                  <p class="search-results-container__button-text--filtering">
                    Filtrér
                    <span
                      v-if="summaryItems.length > 0"
                      class="search-indicator"
                    ></span>
                  </p>
                </div>
              </template>
            </FilteringMenu>
          </div>
          <div
            class="search-results-container__button-container overlay-master"
          >
            <button
              v-if="!hideSearchAgent"
              type="button"
              class="button button--highlight search-results-container__button--search-agent"
              @click.prevent="openSearchAgentOverlay()"
            >
              <div
                class="search-results-container__button search-results-container__button--green"
              >
                <img
                  loading="lazy"
                  class="search-results-container__button-icon search-results-container__button-icon--envelope"
                  src="./../../assets/icons/card/contact/envelope.svg"
                  alt="En konvolut"
                />
                <p class="search-results-container__button-text--search-agent">
                  Opret søgeagent
                </p>
              </div>
            </button>
          </div>
          <div
            class="search-results-container__button-container search-results-container__tile--container"
            v-if="adsCount > 0"
          >
            <!-- Tile visning her -->
            <Tile :event-bus="proxyBus" :tile-view="tileView"></Tile>
          </div>
        </div>
      </div>
      <ScrollTopButton
        v-if="!hideScrollTopButton"
        top-element-id="SearchResultsTopContainer"
      />
      <SearchResults
        :number-of-results="numberOfResults"
        :results="results"
        :tile-view-mode="tileView"
        :event-bus="proxyBus"
      />
      <div
        v-if="!hideSearchAgent"
        class="search-results-container__search-agent-container"
      >
        <SearchAgent
          :event-bus="proxyBus"
          :search-request-id="searchValue"
          :search-request-ad-count="numberOfResults"
          :suggested-name="searchAgentSuggestedName"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import mitt from 'mitt'
import { app } from '@/main'

import { setCookie, getCookie, thousandSeparator } from '@/functions'

import {
  LogAdShownInSearchPixel,
  LogSearchAds,
  LogCriteoListing,
} from '@/events'

import FilteringMenu from '@search/Filtering/FilteringMenu.vue'
import Filtering from '@search/Filtering/Filtering.vue'
import Sorting from '@search/Sorting/Sorting.vue'
import Summary from '@search/Summary/Summary.vue'
import Tile from '@search/Tile/Tile.vue'
import SearchResults from '@components/SearchResults/SearchResults.vue'
import ScrollTopButton from '@components/Shared/ScrollTopButton/ScrollTopButton.vue'

import SearchMixin from '@components/Search/search-mixin.vue'
import SearchAgentOverlay from '@components/SearchAgent/SearchAgentOverlay.vue'
import SearchAgent from '@components/SearchAgent/SearchAgent.vue'

export default {
  mixins: [SearchMixin],
  props: {
    initialResults: {
      default: () => {
        return []
      },
      type: Array,
    },
    searchOrigin: { default: null, type: Number },
    searchValue: { default: null, type: Number },
    hideFiltering: { default: false, type: Boolean },
    hideSearchAgent: { default: false, type: Boolean },
    searchAgentSuggestedName: { default: '', type: String },
    searchAgentCountExceeded: { default: false, type: Boolean },
    hideScrollTopButton: { default: false, type: Boolean },
    stopEventLog: { default: false, type: Boolean },
    stopUsingState: { default: false, type: Boolean },
    isRegistered: { default: null, type: Boolean },
    // For at tilfredsstille typescript
    productTypeNameSingular: { default: '', type: String },
    productTypeNamePlural: { default: '', type: String },
  },
  data() {
    return {
      proxyBus: null,
      tileView: 'default',
      pageNumber: 1,
      loading: false,
      results: [],
      firstLoad: true,
      userLoggedIn: false,
      sortItemsList: [],
      countAds: false,
    }
  },
  components: {
    FilteringMenu,
    Filtering,
    Summary,
    Sorting,
    Tile,
    SearchResults,
    SearchAgentOverlay,
    SearchAgent,
    ScrollTopButton,
  },
  computed: {
    productTypeName() {
      return this.numberOfResults === 1
        ? this.productTypeNameSingular
        : this.productTypeNamePlural
    },
  },
  methods: {
    thousandSeparator(value) {
      return thousandSeparator(value)
    },
    changeSort(value) {
      this.setSort(value) // Function found in search-mixin.vue

      this.firstLoad = false
      this.results = []
      this.pageNumber = 1
      this.loadResults()
    },
    changeTileView(tileView) {
      this.tileView = tileView
      setCookie('tile-view', tileView)
    },
    loadMore() {
      if (this.loading) return

      this.pageNumber += 1
      this.loadResults()
    },
    loadResults() {
      if (this.loading) return

      const scrollPosition = document.documentElement.scrollTop

      if (this.firstLoad) {
        this.getFromState()
        this.firstLoad = false
        return
      }

      this.loading = true

      // load with axios
      app.axios
        .post('/Api/Search/Page', {
          pageNumber: this.pageNumber,
          searchOrigin: this.searchOrigin,
          searchValue: this.searchValue,
          sort: this.sort,
        })
        .then(resp => {
          // append data from endpoint
          resp.data.forEach(result => {
            this.results.push(result)
          })

          LogAdShownInSearchPixel(resp.data)
          LogSearchAds()
          LogCriteoListing(resp.data)

          this.loading = false

          nextTick(() => {
            window.scrollTo(0, scrollPosition)
            this.saveInState()
          })
        })
        .catch(() => {
          // an unnamed error occured in backend
          this.loading = false
        })
    },
    getFromState() {
      if (window.history.state === null) return

      const pageNumber = window.history.state.pageNumber
      this.pageNumber = pageNumber

      const sort = window.history.state.sort
      this.sort = sort // Property found in search-mixin.vue

      this.setSort(this.sort) // Function found in search-mixin.vue

      const owner = this
      // Sætter den korrekte sort option i listen
      this.sortItemsList.forEach(item => {
        item.selected = item.value === owner.sort
      })

      const results = window.history.state.results

      nextTick(() => {
        this.results = results
        LogSearchAds()
        LogCriteoListing(results)
      })
    },
    openSearchAgentOverlay() {
      if (!this.userLoggedIn) {
        const owner = this
        app.toast.info(
          'Du skal være logget ind for at oprette en søgeagent. Det er gratis at oprette en profil.',

          () => {
            app.mitt.emit('prompt-login', () => {
              setTimeout(() => {
                owner.openSearchAgentOverlay()
              }, 100)
            })
          },
          'Gå til login',
          'Luk'
        )
      } else {
        this.$refs.searchAgentOverlay.openForm(
          this.searchValue,
          this.searchAgentSuggestedName,
          this.searchAgentCountExceeded
        )
      }
    },
    saveInState() {
      if (this.stopUsingState) {
        return
      }

      window.history.replaceState(
        JSON.parse(
          JSON.stringify({
            pageNumber: this.pageNumber,
            results: this.results,
            sort: this.sort, // Property found in search-mixin.vue
          })
        ),
        '',
        null
      )
    },
    handleFavorites(favorites) {
      this.userLoggedIn = true

      this.results.forEach(ad => {
        if (favorites.includes(ad.id)) {
          nextTick(() => {
            ad.isFavorite = true
          })
        }
      })
    },
    getSortValue() {
      if (
        this.sortItemsList == null ||
        typeof this.sortItemsList == 'undefined'
      )
        return

      const listOfSelectedItems = this.sortItemsList.filter(elm => {
        return elm.selected
      })

      if (listOfSelectedItems.length < 1) return ''

      return listOfSelectedItems[0].value
    },
    setResultItemFavoriteById(adId, boolValue) {
      const resultItem = this.results.filter(item => {
        return item.id === adId
      })
      if (resultItem.length === 0) return
      let item = resultItem[0]
      item.isFavorite = boolValue
      this.saveInState()
    },
    handleFavoriteAddedCallback(adId) {
      this.setResultItemFavoriteById(adId, true)
    },
    handleFavoriteRemovedCallback(adId) {
      this.setResultItemFavoriteById(adId, false)
    },
  },
  created() {
    this.sortItemsList = this.sortItems
    this.proxyBus = mitt()
    this.proxyBus.on('sorting-callback', this.changeSort)
    this.proxyBus.on('tile-view-callback', this.changeTileView)
    this.proxyBus.on('loadMore-callback', this.loadMore)
    app.mitt.on('login-complete', this.handleFavorites)
    app.mitt.on('favorite-added', this.handleFavoriteAddedCallback)
    app.mitt.on('favorite-removed', this.handleFavoriteRemovedCallback)

    const tileView = getCookie('tile-view')
    if (tileView !== null) {
      this.tileView = tileView
    }
  },
  mounted() {
    this.results = this.initialResults
    this.userLoggedIn = this.isRegistered
    this.sort = this.getSortValue()
    this.countAds = true

    // this is the first load with no data in state
    if (window.history.state === null) {
      if (this.results.length > 0) {
        if (!this.stopEventLog) {
          LogAdShownInSearchPixel(this.results)
        }

        this.saveInState()
      }
    }

    this.loadResults()
  },
}
</script>

<style lang="scss">
@import 'SearchResultsContainer.scss';
</style>
